import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";

export interface SntButtonGroupOption<T extends string> {
  value: T;
  label?: string;
  icon?: ReactNode;
  title?: string;
}

export interface SntButtonGroupProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SntButtonGroupOption<T>[];
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const Track = styled.div<{ $disabled: boolean }>`
  position: relative;
  display: inline-flex;
  background: #eaeef1;
  border-radius: 8px;
  padding: 3px;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
`;

const Thumb = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(29, 40, 60, 0.12), 0 1px 3px rgba(29, 40, 60, 0.08);
  transition: transform 0.22s ease, width 0.22s ease;
  pointer-events: none;
  z-index: 0;
`;

const Segment = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  padding: 6px 14px;
  font-size: 14px;
  line-height: 1.4;
  color: ${(p) => (p.$active ? "#1d283c" : "#424b59")};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s ease;

  &:focus-visible {
    outline: 2px solid #0071a1;
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

function SntButtonGroup<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  className = "",
  style,
}: SntButtonGroupProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

  const activeIdx = Math.max(0, options.findIndex((o) => o.value === value));

  useLayoutEffect(() => {
    const seg = segmentRefs.current[activeIdx];
    const track = trackRef.current;
    if (!seg || !track) return;
    setThumb({ left: seg.offsetLeft, width: seg.offsetWidth });
  }, [activeIdx, options.length]);

  return (
    <Track ref={trackRef} $disabled={disabled} className={className} style={style} role="tablist">
      {thumb && (
        <Thumb
          style={{
            transform: `translateX(${thumb.left}px)`,
            width: thumb.width,
          }}
        />
      )}
      {options.map((opt, idx) => {
        const isActive = opt.value === value;
        return (
          <Segment
            key={opt.value}
            ref={(el) => {
              segmentRefs.current[idx] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            $active={isActive}
            title={opt.title || opt.label}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
          >
            {opt.icon}
            {opt.label}
          </Segment>
        );
      })}
    </Track>
  );
}

export default SntButtonGroup;
